/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => String(id) !== String(productId));
    viewedProducts.unshift(String(productId));
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    this.#write(viewedProducts);
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Removes a single product from the recently viewed list.
   * @param {string} productId - The ID of the product to remove.
   */
  static removeProduct(productId) {
    this.#write(this.getProducts().filter((/** @type {string} */ id) => String(id) !== String(productId)));
  }

  /**
   * Retrieves the list of recently viewed products from localStorage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.map(String).slice(0, this.#MAX_PRODUCTS) : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string[]} products
   */
  static #write(products) {
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(products));
    } catch {
      // Ignore quota / private-mode failures.
    }
  }
}

/**
 * Persists recent search queries in localStorage.
 */
export class SearchHistory {
  /** @static @constant {string} */
  static #STORAGE_KEY = 'searchHistory';
  /** @static @constant {number} */
  static #MAX_QUERIES = 12;
  /** @static @constant {number} */
  static #MIN_LENGTH = 2;

  /**
   * @param {string} query
   */
  static addQuery(query) {
    const normalized = String(query || '')
      .trim()
      .replace(/\s+/g, ' ')
      .slice(0, 80);
    if (normalized.length < this.#MIN_LENGTH) return;

    const lower = normalized.toLowerCase();
    let queries = this.getQueries().filter((item) => {
      const existing = item.toLowerCase();
      if (existing === lower) return false;
      // Drop unfinished prefixes of this completed query ("woo" when saving "wooden").
      if (lower.startsWith(existing) && existing.length < lower.length) return false;
      return true;
    });
    queries.unshift(normalized);
    queries = queries.slice(0, this.#MAX_QUERIES);
    this.#write(queries);
  }

  /**
   * @param {string} query
   */
  static removeQuery(query) {
    this.#write(this.getQueries().filter((item) => item.toLowerCase() !== String(query || '').trim().toLowerCase()));
  }

  static clearQueries() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * @returns {string[]}
   */
  static getQueries() {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string' && item.trim()) : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string[]} queries
   */
  static #write(queries) {
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(queries));
    } catch {
      // Ignore quota / private-mode failures.
    }
  }
}
